import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { PageHeading } from 'components/PageHeading';
import { Breadcrumb } from '../../components/Breadcrumb';
import { PageHeader } from '../../components/PageHeader';
import { useTranslation } from '../../contexts/translation-context';
import { useCarrierPortalSupplier } from './CarrierPortalCarrierContext';
import { SelfBillingMode } from 'src/types/enums';
import { useAuth } from 'contexts/auth-context';
import { CalendarIcon, GearIcon, InfoIcon, MoneyIcon, TruckIcon } from '@phosphor-icons/react';

export interface IHomeCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  to: string;
}

const HomeCard: React.FC<IHomeCardProps> = (props) => {
  const { icon, title, description, to } = props;

  return (
    <Link to={to} className="card p-16">
      <div className="text-fg-muted mb-2 flex justify-center">{icon}</div>
      <div className="font-medium text-fg-secondary text-center border-b border-border pb-4">{title}</div>
      <div className="pt-4 text-center whitespace-normal">{description}</div>
    </Link>
  );
};

export const CarrierPortalHomePage = () => {
  const { me } = useAuth();
  const { supplier } = useCarrierPortalSupplier();
  const { i18n, lang } = useTranslation();
  const isDispatcher = me.carriers.some((c) => c.role === 'CarrierDispatcher');

  const cards = useMemo(() => {
    const items: IHomeCardProps[] = [
      {
        to: `/carrier-portal/${supplier.id}/planning`,
        title: i18n('carrierPortal.planning.planning'),
        icon: <CalendarIcon className="w-8 h-8" />,
        description: i18n('carrierPortal.planning.managePlanning'),
      },
      {
        to: `/carrier-portal/${supplier.id}/orders`,
        title: i18n('carrierPortal.orders.orders'),
        icon: <TruckIcon className="w-8 h-8" />,
        description: i18n('carrierPortal.orders.manageOrders'),
      },
    ];

    if (isDispatcher && (supplier.selfBillingMode !== SelfBillingMode.None || supplier.hasSelfBills)) {
      // Ride-overview wording unless the carrier is self-billed now (also for a carrier set to None).
      const isRideOverview = supplier.selfBillingMode !== SelfBillingMode.SelfBilling;
      items.push({
        to: `/carrier-portal/${supplier.id}/self-billing`,
        title: i18n(
          isRideOverview ? 'carrierPortal.selfBilling.rideOverview' : 'carrierPortal.selfBilling.selfBilling',
        ),
        icon: <MoneyIcon className="w-8 h-8" />,
        description: i18n(
          isRideOverview
            ? 'carrierPortal.selfBilling.manageRideOverview'
            : 'carrierPortal.selfBilling.manageSelfBilling',
        ),
      });
    }

    items.push({
      to: `/carrier-portal/${supplier.id}/knowledge-base`,
      title: i18n('carrierPortal.generalInformation.generalInformation'),
      icon: <InfoIcon className="w-8 h-8" />,
      description: i18n('carrierPortal.home.infoSubtitle'),
    });

    if (isDispatcher) {
      items.push({
        to: `/carrier-portal/${supplier.id}/business-information/info`,
        title: i18n('carrierPortal.businessInformation.businessInformation'),
        icon: <GearIcon className="w-8 h-8" />,
        description: i18n('carrierPortal.businessInformation.settingsAndBusinessInformation'),
      });
    }

    return items;
  }, [i18n, lang, isDispatcher, supplier.id, supplier.selfBillingMode, supplier.hasSelfBills]);

  return (
    <div>
      <PageHeader
        title={i18n('carrierPortal.home.title', {
          customerName: supplier.name,
        })}
      />

      <PageHeading leftSide={<Breadcrumb currentItem={i18n('carrierPortal.home.carrierPortal')} />} />

      <div className="px-8 mb-8">
        <h1 className="flex gap-1 heading-one mb-4">
          <span>{i18n('carrierPortal.home.hello')}</span>
          <span className="text-brand">{`${me.name}!`}</span>
        </h1>

        <p>{i18n('carrierPortal.home.welcome')}</p>
      </div>

      <div className="grid xl:grid-cols-3 md:grid-cols-2 gap-4 px-8">
        {cards.map((card) => (
          <HomeCard key={card.to} {...card} />
        ))}
      </div>
    </div>
  );
};
